import React, { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import styled, { css } from "styled-components";
import useHistory from "routing/useHistory";
import { useQueryParam, StringParam } from "routing/QueryParams";
import SecurityRoutes from "security-app/routing/Routes";
import { useGetInvestigationDetails } from "security-app/hooks/useInvestigationsAPI";
import useInvestigationPanel from "security-app/components/Investigations/hooks/useInvestigationPanel";
import { Icon, Spinner, IconButton } from "components/common";
import ActiveInvestigationLabel from "security-app/components/Investigations/ActiveInvestigationLabel";
import { Button, Row as BsRow, Col, Label, SegmentedControl } from "components/bootstrap";
import InvestigationMoreActionsMenu from "security-app/components/Investigations/InvestigationMoreActionsMenu";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import AlertsModals from "security-app/components/SecurityEvents/Alerts/AlertsModals";
import { ModalProvider } from "common/contexts";
import { Row } from "common/components";
import AIReportButton from "security-app/components/Investigations/AIReport/AIReportButton";
import useIsInvestigationAIReportEnabled from "security-app/components/Investigations/AIReport/hooks/useIsInvestigationAIReportEnabled";
import useFeature from "hooks/useFeature";
import { AGENT_WORKFLOW_FEATURE_FLAG } from "aiagent/Constants";
import DateStat from "./DateStat";
import OverviewSegment from "./OverviewSegment";
import EditInvestigation from "./EditInvestigation";
import MainDetails from "./MainDetails";
import TimelineSegment from "./TimelineSegment";
import ProceduresSegment from "./ProceduresSegment";
import AIAgentTriageInvestigation from "../AIAgentTriageInvestigation";
const OVERVIEW_SEGMENT = "overview";
const TIMELINE_SEGMENT = "timeline";
const PROCEDURES_SEGMENT = "procedures";
const AI_AGENT_TRIAGE_SEGMENT = "ai_agent_triage";
const DETAILS_SEGMENTS = [
  {
    value: OVERVIEW_SEGMENT,
    label: "Overview"
  },
  {
    value: PROCEDURES_SEGMENT,
    label: "Procedures"
  },
  {
    value: TIMELINE_SEGMENT,
    label: "Timeline"
  }
];
const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
`;
const Header = styled.div(
  ({ $inModal }) => css`
    display: flex;
    justify-content: space-between;
    padding-top: 30px;
    margin-bottom: 20px;
    gap: 15px;
    margin-left: ${$inModal ? "none" : "-15px"};
    margin-right: ${$inModal ? "none" : "-15px"};
  `
);
const LeftCol = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;
const RightCol = styled.div`
  display: flex;
  gap: 30px;
`;
const StyledRow = styled(BsRow)`
  ${({ $inModal }) => $inModal && css`
      margin-left: 0;
      margin-right: 0;
    `}
`;
const MainDetailsLeft = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;
const SegmentContainer = styled(BsRow)`
  flex: 1;

  ${({ $inModal }) => $inModal && css`
      margin-left: 0;
      margin-right: 0;
    `}
`;
const FullHeightCol = styled(Col)`
  height: 100%;
`;
const isValidSegment = (segment) => segment === OVERVIEW_SEGMENT || segment === TIMELINE_SEGMENT || segment === PROCEDURES_SEGMENT || segment === AI_AGENT_TRIAGE_SEGMENT;
const useCurrentSegment = () => {
  const [currentSegment, setCurrentSegment] = useQueryParam("segment", StringParam);
  const _setCurrentSegment = useCallback(
    (newSegment) => {
      setCurrentSegment(newSegment);
    },
    [setCurrentSegment]
  );
  return [isValidSegment(currentSegment) ? currentSegment : OVERVIEW_SEGMENT, _setCurrentSegment];
};
const InvestigationDetails = ({
  investigation,
  inModal = false
}) => {
  var _a;
  const sendTelemetry = useSendTelemetry("investigation");
  const isAgentWorkflowEnabled = useFeature(AGENT_WORKFLOW_FEATURE_FLAG);
  const showTriageTab = isAgentWorkflowEnabled && (investigation == null ? void 0 : investigation.has_agent_initial_assessment);
  const isAIReportFeatureEnabled = useIsInvestigationAIReportEnabled();
  const { openInvestigation, selectedInvestigationId, setSelectedInvestigationId } = useInvestigationPanel();
  const { push } = useHistory();
  const [currentSegment, setCurrentSegment] = useCurrentSegment();
  const [showEditModal, setShowEditModal] = useState(false);
  const toggleEditModal = useCallback(() => setShowEditModal((cur) => !cur), []);
  const _setCurrentSegment = useCallback(
    (newSegment) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.INVESTIGATION_TIMELINE_FILTER_UPDATED, {
        app_pathname: "security",
        event_details: {
          section: newSegment
        }
      });
      setCurrentSegment(newSegment);
    },
    [sendTelemetry, setCurrentSegment]
  );
  const finalDetailsSegments = showTriageTab ? [
    ...DETAILS_SEGMENTS,
    {
      value: AI_AGENT_TRIAGE_SEGMENT,
      label: "AI Agent Triage"
    }
  ] : DETAILS_SEGMENTS;
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(Header, { $inModal: inModal }, /* @__PURE__ */ React.createElement(LeftCol, null, !inModal && /* @__PURE__ */ React.createElement(Button, { onClick: () => push(SecurityRoutes.INVESTIGATIONS.LIST) }, /* @__PURE__ */ React.createElement(Icon, { name: "arrow_left_alt", size: "sm" }), " Back"), /* @__PURE__ */ React.createElement("h1", null, investigation.name), investigation.archived && /* @__PURE__ */ React.createElement(Label, { bsStyle: "warning", bsSize: "xsmall" }, "Archived"), !investigation.archived && (investigation.id === selectedInvestigationId ? /* @__PURE__ */ React.createElement(ActiveInvestigationLabel, null, "Active") : /* @__PURE__ */ React.createElement(Button, { bsSize: "xs", onClick: () => setSelectedInvestigationId(investigation.id) }, "Set as active")), /* @__PURE__ */ React.createElement(
    InvestigationMoreActionsMenu,
    {
      investigation,
      isArchived: investigation.archived,
      onEdit: toggleEditModal
    }
  )), /* @__PURE__ */ React.createElement(RightCol, null, /* @__PURE__ */ React.createElement(DateStat, { date: investigation.created_at, description: "Days Open" }), /* @__PURE__ */ React.createElement(DateStat, { date: investigation.updated_at, description: "Latest Activity" }))), /* @__PURE__ */ React.createElement(StyledRow, { className: "content no-bm", $inModal: inModal }, /* @__PURE__ */ React.createElement(Col, { xs: 12 }, /* @__PURE__ */ React.createElement(Row, { $justify: "space-between" }, /* @__PURE__ */ React.createElement(MainDetailsLeft, null, /* @__PURE__ */ React.createElement(
    SegmentedControl,
    {
      data: finalDetailsSegments,
      value: currentSegment,
      onChange: _setCurrentSegment
    }
  )), /* @__PURE__ */ React.createElement(Row, { $justify: "flex-end", $align: "center", $gap: "md" }, /* @__PURE__ */ React.createElement(
    MainDetails,
    {
      priority: investigation.priority,
      status: investigation.status,
      assignedTo: investigation.assigned_to
    }
  ), isAIReportFeatureEnabled && /* @__PURE__ */ React.createElement(AIReportButton, { investigation, logsCount: (_a = investigation.logs_count) != null ? _a : 0 }), /* @__PURE__ */ React.createElement(
    IconButton,
    {
      name: "right_panel_open",
      title: "Open in Sidebar",
      size: "lg",
      onClick: () => openInvestigation(investigation.id)
    }
  ))))), /* @__PURE__ */ React.createElement(SegmentContainer, { className: "content", $inModal: inModal }, /* @__PURE__ */ React.createElement(FullHeightCol, { xs: 12 }, currentSegment === OVERVIEW_SEGMENT && /* @__PURE__ */ React.createElement(OverviewSegment, { investigation }), currentSegment === PROCEDURES_SEGMENT && /* @__PURE__ */ React.createElement(ProceduresSegment, null), currentSegment === TIMELINE_SEGMENT && /* @__PURE__ */ React.createElement(TimelineSegment, { investigationId: investigation.id }), showTriageTab && currentSegment === AI_AGENT_TRIAGE_SEGMENT && /* @__PURE__ */ React.createElement(AIAgentTriageInvestigation, { investigationId: investigation.id }))), showEditModal && /* @__PURE__ */ React.createElement(EditInvestigation, { investigation, onClose: toggleEditModal, show: true }));
};
const DetailsWrapper = ({
  investigation = void 0,
  inModal = false
}) => {
  const { id: investigationsId } = useParams();
  const { push } = useHistory();
  const { loadingInvestigationDetails, fetchError, currentInvestigation } = useGetInvestigationDetails(investigationsId);
  if (loadingInvestigationDetails) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading investigation details..." });
  }
  if (fetchError) {
    push(SecurityRoutes.INVESTIGATIONS.LIST);
  }
  if (!fetchError) {
    return /* @__PURE__ */ React.createElement(ModalProvider, null, /* @__PURE__ */ React.createElement(AlertsModals, null), /* @__PURE__ */ React.createElement(InvestigationDetails, { investigation: investigation || currentInvestigation, inModal }));
  }
  return null;
};
export default DetailsWrapper;
